import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    Avatar,
    Grid,
    Chip,
    Divider,
    Button,
    CircularProgress,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Menu,
    MenuItem,
    Card,
    CardContent,
    Alert,
    Stack
} from '@mui/material';
import { useNotification } from '../contexts/NotificationContext';
import { createChatRoom } from '../services/firebase';
import MessageIcon from '@mui/icons-material/Message';
import { useAuth } from '../contexts/AuthContext';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CurrencyYenIcon from '@mui/icons-material/CurrencyYen';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TimerIcon from '@mui/icons-material/Timer';
import SmokingRoomsIcon from '@mui/icons-material/SmokingRooms';
import PetsIcon from '@mui/icons-material/Pets';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import axios from 'axios';
import { toggleFavoriteUser, getUser, getUserRooms, clearCacheByPattern } from '../services/api';
import { formatLastLoginTime } from '../utils/dateUtils';
import ReportDialog from '../components/common/ReportDialog';
import Seo from '../components/common/Seo';
import { getUserSchema, getBreadcrumbSchema } from '../utils/structuredDataHelpers';
import Breadcrumbs from '../components/common/Breadcrumbs';
import { getUserDetailTitle, getUserDetailDescription } from '../utils/seoHelpers';
import { event } from '../utils/analytics';

// axiosのデフォルト設定を追加
axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('token')}`;

// 入居希望時期の日本語変換マップ
const MOVE_IN_PREFERENCE_MAP = {
    'within 1 month': '1ヶ月以内',
    'within 3 months': '3ヶ月以内',
    'within 6 months': '6ヶ月以内',
    'more than 6 months': '6ヶ月以上'
};

// 滞在期間の日本語変換マップ
const STAY_DURATION_MAP = {
    short: '短期（1-3ヶ月）',
    medium: '中期（3-6ヶ月）',
    long: '長期（6ヶ月以上）'
};

// マッチング条件の日本語変換マップ
const MATCHING_PREFERENCES_MAP = {
    lifeRhythm: {
        label: '生活リズム',
        icon: AccessTimeIcon,
        morning: '朝型',
        night: '夜型'
    },
    cleanliness: {
        label: '清潔さ',
        icon: HomeIcon,
        clean: 'きれい好き',
        relaxed: 'ある程度の散らかりはOK'
    },
    communication: {
        label: 'コミュニケーション',
        icon: MessageIcon,
        active: '積極的に交流',
        minimal: '必要最低限の交流'
    },
    stayingHome: {
        label: '在宅状況',
        icon: HomeIcon,
        often: '在宅が多い',
        rarely: '外出が多い'
    },
    visitors: {
        label: '来客',
        icon: PersonIcon,
        welcome: '友人を招くことがある',
        rarely: 'ほとんど招かない'
    },
    smoking: {
        label: '喫煙',
        icon: SmokingRoomsIcon,
        'non-smoker': '喫煙しない',
        'smoker': '喫煙する'
    },
    pets: {
        label: 'ペット',
        icon: PetsIcon,
        'no': 'ペットを飼っていない',
        'yes': 'ペットを飼っている'
    }
};

function UserDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { showNotification } = useNotification();
    const { currentUser, blockedUsers, setBlockedUsers } = useAuth();
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isFavorite, setIsFavorite] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [blockAction, setBlockAction] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const openMenu = Boolean(anchorEl);
    const [userRooms, setUserRooms] = useState([]);
    const [roomsLoading, setRoomsLoading] = useState(true);
    const [canContact, setCanContact] = useState(true);
    const [contactErrorMessage, setContactErrorMessage] = useState('');
    const [contactLoading, setContactLoading] = useState(false);

    // AuthContextのblockedUsersを使用してブロック状態を判定
    const isBlocked = useMemo(() => {
        return blockedUsers.includes(id);
    }, [blockedUsers, id]);

    // ユーザー情報、物件情報、連絡可否を一度に取得する統合された関数
    const fetchUserData = useCallback(async () => {
        try {
            setIsLoading(true);

            // キャッシュ機能付きでユーザー情報を取得
            const userData = await getUser(id);
            setUser(userData);

            // お気に入り状態を設定
            if (currentUser && userData.profile.favorites?.users) {
                setIsFavorite(userData.profile.favorites.users.includes(userData._id));
            }

            // ユーザープロフィール表示イベントをトラッキング
            event({
                category: 'User',
                action: 'Viewed User Profile',
                label: userData.profile?.nickname || `User ID: ${userData._id}`
            });

            // 並行してユーザーの物件情報を取得（キャッシュ機能付き）
            const roomsPromise = getUserRooms(userData._id)
                .then(response => {
                    setUserRooms(response.rooms);
                })
                .catch(error => {
                    console.error('物件一覧の取得に失敗しました:', error);
                })
                .finally(() => {
                    setRoomsLoading(false);
                });

            // 連絡可否チェック（同性のみ募集の場合のみ）
            let contactCheckPromise = Promise.resolve();
            if (currentUser && userData.profile?.sameGenderOnly && userData._id !== currentUser.uid) {
                contactCheckPromise = fetch(`/api/users/check-gender-compatibility`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({ targetUserId: userData._id })
                })
                    .then(response => {
                        if (!response.ok) {
                            return response.json().then(error => {
                                setCanContact(false);
                                setContactErrorMessage(error.message);
                            });
                        }
                    })
                    .catch(error => {
                        console.error('連絡可否チェックエラー:', error);
                        setCanContact(false);
                        setContactErrorMessage('連絡可能性の確認に失敗しました');
                    });
            }

            // 全ての非同期処理を並行実行
            await Promise.all([roomsPromise, contactCheckPromise]);

        } catch (error) {
            console.error('ユーザー取得エラー:', error);
            showNotification('ユーザー情報の取得に失敗しました', 'error');
            navigate('/renters');
        } finally {
            setIsLoading(false);
        }
    }, [id, currentUser, navigate, showNotification]);

    // ユーザーIDが変更された時のみデータを取得
    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    // ブロック操作の最適化（AuthContextの状態を直接更新）
    const handleBlockToggle = async () => {
        try {
            if (isBlocked) {
                await axios.delete(`/api/users/unblock/${user._id}`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                setBlockedUsers(blockedUsers.filter(id => id !== user._id));
            } else {
                await axios.post(`/api/users/block/${user._id}`, {}, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                setBlockedUsers([...blockedUsers, user._id]);
            }

            // ブロック状態変更後、関連キャッシュをクリア
            clearCacheByPattern('/users/blocked');

            handleBlockDialogClose();
        } catch (error) {
            console.error('ブロック操作に失敗:', error);
        }
    };

    const handleBlockDialogOpen = () => {
        setBlockAction(isBlocked ? 'unblock' : 'block');
        setOpenDialog(true);
    };

    const handleBlockDialogClose = () => {
        setOpenDialog(false);
        setBlockAction(null);
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleBlockAction = () => {
        handleBlockDialogOpen();
        handleMenuClose();
    };

    const handleFavoriteToggle = async () => {
        if (!currentUser) {
            showNotification('お気に入りにはログインが必要です', 'warning');
            return;
        }

        try {
            await toggleFavoriteUser(user._id);
            setIsFavorite(!isFavorite);
            showNotification(
                isFavorite ? 'お気に入りから削除しました' : 'お気に入りに追加しました',
                'success'
            );

            // お気に入り登録/解除イベントをトラッキング
            event({
                category: 'User',
                action: isFavorite ? 'Removed User Favorite' : 'Added User Favorite',
                label: user.profile?.nickname || `User ID: ${user._id}`
            });
        } catch (error) {
            showNotification('お気に入りの更新に失敗しました', 'error');
        }
    };

    const handleFavoriteAction = () => {
        handleFavoriteToggle();
        handleMenuClose();
    };

    const handleReportUser = () => {
        handleMenuClose();
        setReportDialogOpen(true);
    };

    // SEO設定
    const getPageTitle = () => {
        if (!user) return 'ユーザー詳細 | Room Share Matching';
        return getUserDetailTitle(user);
    };

    const getPageDescription = () => {
        if (!user) return 'ルームシェアパートナーの詳細情報';
        return getUserDetailDescription(user);
    };

    // パンくずリストの項目を生成する関数
    const getBreadcrumbs = () => {
        const items = [
            { name: 'トップ', path: '/' },
            { name: 'ルームメイト検索', path: '/renters' }
        ];

        if (user && user.profile) {
            // 希望エリアがある場合は地域のパンくずを追加
            const preferredArea = user.profile.preferences?.preferredAreas?.[0];
            if (preferredArea) {
                if (preferredArea.prefecture) {
                    items.push({
                        name: preferredArea.prefecture,
                        path: `/renters?area=${encodeURIComponent(preferredArea.prefecture)}`
                    });
                }
                if (preferredArea.city) {
                    items.push({
                        name: preferredArea.city,
                        path: `/renters?area=${encodeURIComponent(`${preferredArea.prefecture} ${preferredArea.city}`)}`
                    });
                }
            }

            // ユーザー名を追加
            items.push({
                name: `${user.profile.nickname || 'ユーザー'}のプロフィール`,
                path: `/users/${id}`
            });
        } else {
            items.push({
                name: 'ユーザープロフィール',
                path: `/users/${id}`
            });
        }

        return items;
    };

    // JSON-LDの配列を生成
    const getSchemaArray = () => {
        if (!user || !user.profile) return null;

        // ユーザー情報とパンくずリストの構造化データを配列で結合
        const userSchemaObj = getUserSchema(user);
        const breadcrumbSchema = getBreadcrumbSchema(getBreadcrumbs());

        return [userSchemaObj, breadcrumbSchema].filter(Boolean);
    };

    if (isLoading) {
        return (
            <Container maxWidth="lg">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (!user) {
        return (
            <Container maxWidth="lg">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <Typography variant="h5" color="text.secondary">
                        ユーザーが見つかりませんでした
                    </Typography>
                </Box>
            </Container>
        );
    }

    const profile = user.profile;
    const age = profile.dateOfBirth ?
        Math.floor((new Date() - new Date(profile.dateOfBirth)) / (365.25 * 24 * 60 * 60 * 1000)) :
        null;
    const preferredArea = profile.preferences?.preferredAreas?.[0] || {};

    // 該当ユーザーのマッチング条件が設定されているか確認
    const hasMatchingPreferences = profile.matchingPreferences &&
        Object.values(profile.matchingPreferences).some(value => value !== null);

    // マッチング条件の表示内容を取得
    const getMatchingPreferenceValue = (key) => {
        const value = profile.matchingPreferences?.[key];
        if (!value) return '未設定';
        return MATCHING_PREFERENCES_MAP[key][value];
    };

    const handleStartChat = async () => {
        if (!currentUser) {
            navigate('/login', { state: { from: location.pathname } });
            return;
        }

        if (!canContact) {
            showNotification(contactErrorMessage, 'error');
            return;
        }

        // 重複実行防止
        if (contactLoading) {
            return;
        }

        try {
            setContactLoading(true);

            // 既に性別チェック済みなので、createChatRoomでのチェックをスキップ
            const roomId = await createChatRoom(currentUser.uid, user._id, null, true);
            navigate(`/messages/${roomId}`);

            // ユーザーへの連絡イベントをトラッキング
            event({
                category: 'Contact',
                action: 'Contacted User',
                label: user.profile?.nickname || `User ID: ${user._id}`
            });
        } catch (error) {
            console.error('チャットの開始に失敗しました:', error);
            showNotification(error.message || 'チャットの開始に失敗しました', 'error');
        } finally {
            setContactLoading(false);
        }
    };

    // 希望エリアのテキスト生成
    const getAreaText = () => {
        const parts = [];
        if (preferredArea.prefecture) parts.push(preferredArea.prefecture);
        if (preferredArea.city) parts.push(preferredArea.city);
        if (preferredArea.line) parts.push(preferredArea.line);
        if (preferredArea.station) parts.push(`${preferredArea.station}駅`);
        return parts.length > 0 ? parts.join(' ') : '未設定';
    };

    return (
        <Container maxWidth="lg">
            <Seo
                title={getPageTitle()}
                description={getPageDescription()}
                canonical={`/users/${id}`}
                schema={getSchemaArray()}
                noindex={user?.isSample}
            />

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
                    <CircularProgress />
                </Box>
            ) : !user ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <Typography variant="h5" color="text.secondary">
                        ユーザーが見つかりませんでした
                    </Typography>
                </Box>
            ) : (
                <>
                    <Breadcrumbs items={getBreadcrumbs()} />

                    <Paper sx={{ p: 4, mt: 3, borderRadius: 2, position: 'relative' }}>
                        {/* サンプルユーザーバッジ */}
                        {user.isSample && (
                            <Chip
                                label="サンプルユーザー"
                                color="primary"
                                size="small"
                                sx={{
                                    position: 'absolute',
                                    top: 16,
                                    right: 16,
                                    bgcolor: 'rgba(25, 118, 210, 0.8)',
                                    color: 'white'
                                }}
                            />
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box>
                                <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mr: 2 }}>
                                    {profile.nickname}
                                </Typography>
                                {/* 同性のみ表示 */}
                                {profile.sameGenderOnly && (
                                    <Chip
                                        color="secondary"
                                        label="同性のみ"
                                        sx={{ ml: 1 }}
                                    />
                                )}
                                {formatLastLoginTime(user.lastLoginAt) && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                        <AccessTimeIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {formatLastLoginTime(user.lastLoginAt)}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            {currentUser && currentUser.uid !== user._id && (
                                <>
                                    <Tooltip title="オプション">
                                        <IconButton onClick={handleMenuOpen}>
                                            <MoreHorizIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Menu
                                        anchorEl={anchorEl}
                                        open={openMenu}
                                        onClose={handleMenuClose}
                                    >
                                        <MenuItem onClick={handleFavoriteAction}>
                                            {isFavorite ? 'お気に入りから削除' : 'お気に入りに追加'}
                                        </MenuItem>
                                        <MenuItem onClick={handleBlockAction} disabled={user.isSample}>
                                            {isBlocked ? 'ブロック解除' : 'ユーザーをブロック'}
                                        </MenuItem>
                                        <MenuItem onClick={handleReportUser} disabled={user.isSample}>
                                            ユーザーを通報
                                        </MenuItem>
                                    </Menu>
                                </>
                            )}
                        </Box>

                        {/* 連絡制限の警告 */}
                        {!canContact && contactErrorMessage && (
                            <Alert severity="info" sx={{ mb: 3 }}>
                                {contactErrorMessage}
                            </Alert>
                        )}

                        {/* プロフィール基本情報 */}
                        <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
                            <CardContent>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                }}>
                                    <Avatar
                                        src={profile.profileImage}
                                        sx={{
                                            width: 120,
                                            height: 120,
                                            mr: { xs: 0, sm: 3 },
                                            mb: { xs: 2, sm: 0 },
                                            border: '4px solid',
                                            borderColor: 'primary.light'
                                        }}
                                    />
                                    <Box sx={{ minWidth: 0, width: '100%' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                            <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                            <Typography variant="h6" color="text.secondary">
                                                {age}歳 • {profile.gender === 'male' ? '男性' : '女性'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <WorkIcon sx={{ fontSize: 20, mr: 1, color: 'text.secondary' }} />
                                            <Typography variant="subtitle1" color="text.secondary">
                                                {profile.occupation || '職業未設定'}
                                            </Typography>
                                        </Box>

                                        {/* 認証バッジ */}
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {user.emailVerified && (
                                                <Chip
                                                    icon={<EmailIcon />}
                                                    label="メール認証済み"
                                                    color="success"
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ borderRadius: 2 }}
                                                />
                                            )}
                                            {user.phoneVerified && (
                                                <Chip
                                                    icon={<PhoneIcon />}
                                                    label="電話番号認証済み"
                                                    color="success"
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ borderRadius: 2 }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* 自己紹介 */}
                        <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                                    自己紹介
                                </Typography>
                                <Typography
                                    sx={{
                                        whiteSpace: 'pre-line',
                                        pl: 4,
                                        lineHeight: 1.8
                                    }}
                                >
                                    {profile.introduction || '自己紹介文が未設定です'}
                                </Typography>
                            </CardContent>
                        </Card>

                        {/* 希望条件 */}
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                            希望条件
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
                                    <CardContent>
                                        <Box sx={{ mb: 3 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <CurrencyYenIcon sx={{ color: 'primary.main', mr: 1 }} />
                                                <Typography variant="subtitle2">希望家賃</Typography>
                                            </Box>
                                            <Typography sx={{ pl: 4, fontWeight: 'medium', whiteSpace: 'nowrap' }}>
                                                ¥{profile.preferences?.rentRange?.min?.toLocaleString() || 0} ~ ¥{profile.preferences?.rentRange?.max?.toLocaleString() || 0}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <LocationOnIcon sx={{ color: 'primary.main', mr: 1 }} />
                                                <Typography variant="subtitle2">希望エリア</Typography>
                                            </Box>
                                            <Typography sx={{
                                                pl: 4, fontWeight: 'medium',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden'
                                            }}>
                                                {getAreaText()}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
                                    <CardContent>
                                        <Box sx={{ mb: 3 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <CalendarTodayIcon sx={{ color: 'primary.main', mr: 1 }} />
                                                <Typography variant="subtitle2">入居希望時期</Typography>
                                            </Box>
                                            <Typography sx={{ pl: 4, fontWeight: 'medium' }}>
                                                {MOVE_IN_PREFERENCE_MAP[profile.moveInPreference] || '未設定'}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ mb: 3 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <TimerIcon sx={{ color: 'primary.main', mr: 1 }} />
                                                <Typography variant="subtitle2">居住希望期間</Typography>
                                            </Box>
                                            <Typography sx={{ pl: 4, fontWeight: 'medium' }}>
                                                {STAY_DURATION_MAP[profile.stayDuration] || '未設定'}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>ライフスタイル</Typography>
                                            <Stack spacing={2} sx={{ pl: 4 }}>
                                                {/* 喫煙 */}
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>喫煙</Typography>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Chip
                                                            icon={<SmokingRoomsIcon />}
                                                            label="喫煙しない"
                                                            color={profile.matchingPreferences?.smoking === 'non-smoker' ? "primary" : "default"}
                                                            variant={profile.matchingPreferences?.smoking === 'non-smoker' ? "filled" : "outlined"}
                                                            size="small"
                                                            sx={{ borderRadius: 2 }}
                                                        />
                                                        <Chip
                                                            icon={<SmokingRoomsIcon />}
                                                            label="喫煙する"
                                                            color={profile.matchingPreferences?.smoking === 'smoker' ? "primary" : "default"}
                                                            variant={profile.matchingPreferences?.smoking === 'smoker' ? "filled" : "outlined"}
                                                            size="small"
                                                            sx={{ borderRadius: 2 }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* ペット */}
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>ペット</Typography>
                                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                        <Chip
                                                            icon={<PetsIcon />}
                                                            label="ペットを飼っていない"
                                                            color={profile.matchingPreferences?.pets === 'no' ? "primary" : "default"}
                                                            variant={profile.matchingPreferences?.pets === 'no' ? "filled" : "outlined"}
                                                            size="small"
                                                            sx={{ borderRadius: 2 }}
                                                        />
                                                        <Chip
                                                            icon={<PetsIcon />}
                                                            label="ペットを飼っている"
                                                            color={profile.matchingPreferences?.pets === 'yes' ? "primary" : "default"}
                                                            variant={profile.matchingPreferences?.pets === 'yes' ? "filled" : "outlined"}
                                                            size="small"
                                                            sx={{ borderRadius: 2 }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* マッチング条件 */}
                                                {profile.matchingPreferences?.lifeRhythm && (
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>生活リズム</Typography>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Chip
                                                                icon={<AccessTimeIcon />}
                                                                label="朝型"
                                                                color={profile.matchingPreferences.lifeRhythm === 'morning' ? "primary" : "default"}
                                                                variant={profile.matchingPreferences.lifeRhythm === 'morning' ? "filled" : "outlined"}
                                                                size="small"
                                                                sx={{ borderRadius: 2 }}
                                                            />
                                                            <Chip
                                                                icon={<AccessTimeIcon />}
                                                                label="夜型"
                                                                color={profile.matchingPreferences.lifeRhythm === 'night' ? "primary" : "default"}
                                                                variant={profile.matchingPreferences.lifeRhythm === 'night' ? "filled" : "outlined"}
                                                                size="small"
                                                                sx={{ borderRadius: 2 }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                )}

                                                {profile.matchingPreferences?.cleanliness && (
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>清潔さ</Typography>
                                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                            <Chip
                                                                icon={<HomeIcon />}
                                                                label="きれい好き"
                                                                color={profile.matchingPreferences.cleanliness === 'clean' ? "primary" : "default"}
                                                                variant={profile.matchingPreferences.cleanliness === 'clean' ? "filled" : "outlined"}
                                                                size="small"
                                                                sx={{ borderRadius: 2 }}
                                                            />
                                                            <Chip
                                                                icon={<HomeIcon />}
                                                                label="ある程度の散らかりはOK"
                                                                color={profile.matchingPreferences.cleanliness === 'relaxed' ? "primary" : "default"}
                                                                variant={profile.matchingPreferences.cleanliness === 'relaxed' ? "filled" : "outlined"}
                                                                size="small"
                                                                sx={{ borderRadius: 2 }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                )}

                                                {profile.matchingPreferences?.communication && (
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>コミュニケーション</Typography>
                                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                            <Chip
                                                                icon={<MessageIcon />}
                                                                label="積極的に交流"
                                                                color={profile.matchingPreferences.communication === 'active' ? "primary" : "default"}
                                                                variant={profile.matchingPreferences.communication === 'active' ? "filled" : "outlined"}
                                                                size="small"
                                                                sx={{ borderRadius: 2 }}
                                                            />
                                                            <Chip
                                                                icon={<MessageIcon />}
                                                                label="必要最低限の交流"
                                                                color={profile.matchingPreferences.communication === 'minimal' ? "primary" : "default"}
                                                                variant={profile.matchingPreferences.communication === 'minimal' ? "filled" : "outlined"}
                                                                size="small"
                                                                sx={{ borderRadius: 2 }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                )}

                                                {profile.matchingPreferences?.stayingHome && (
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>在宅状況</Typography>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Chip
                                                                icon={<HomeIcon />}
                                                                label="在宅が多い"
                                                                color={profile.matchingPreferences.stayingHome === 'often' ? "primary" : "default"}
                                                                variant={profile.matchingPreferences.stayingHome === 'often' ? "filled" : "outlined"}
                                                                size="small"
                                                                sx={{ borderRadius: 2 }}
                                                            />
                                                            <Chip
                                                                icon={<HomeIcon />}
                                                                label="外出が多い"
                                                                color={profile.matchingPreferences.stayingHome === 'rarely' ? "primary" : "default"}
                                                                variant={profile.matchingPreferences.stayingHome === 'rarely' ? "filled" : "outlined"}
                                                                size="small"
                                                                sx={{ borderRadius: 2 }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                )}

                                                {profile.matchingPreferences?.visitors && (
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>来客</Typography>
                                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                            <Chip
                                                                icon={<PersonIcon />}
                                                                label="友人を招くことがある"
                                                                color={profile.matchingPreferences.visitors === 'welcome' ? "primary" : "default"}
                                                                variant={profile.matchingPreferences.visitors === 'welcome' ? "filled" : "outlined"}
                                                                size="small"
                                                                sx={{ borderRadius: 2 }}
                                                            />
                                                            <Chip
                                                                icon={<PersonIcon />}
                                                                label="ほとんど招かない"
                                                                color={profile.matchingPreferences.visitors === 'rarely' ? "primary" : "default"}
                                                                variant={profile.matchingPreferences.visitors === 'rarely' ? "filled" : "outlined"}
                                                                size="small"
                                                                sx={{ borderRadius: 2 }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Stack>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* アクションボタン */}
                        {!currentUser ? (
                            /* 未ログイン時：登録促進UI */
                            <Box sx={{ mt: 4 }}>
                                <Alert
                                    severity="info"
                                    sx={{ mb: 2, borderRadius: 2, alignItems: 'center' }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        この方に連絡するにはログインが必要です
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        アカウント登録は無料・3分で完了します
                                    </Typography>
                                </Alert>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: 2,
                                }}>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        fullWidth
                                        onClick={() => navigate('/login', { state: { from: location.pathname } })}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        ログインする
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        onClick={() => navigate('/register', { state: { from: location.pathname } })}
                                        sx={{
                                            borderRadius: 2,
                                            backgroundColor: 'primary.main',
                                            '&:hover': { backgroundColor: 'primary.dark' }
                                        }}
                                    >
                                        無料登録して連絡する
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            /* ログイン済み：通常ボタン */
                            <Box sx={{
                                mt: 4,
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: 'center',
                                gap: 2
                            }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate(-1)}
                                    size="large"
                                    fullWidth
                                    sx={{
                                        borderRadius: 2,
                                        maxWidth: { xs: '100%', sm: 200 },
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    戻る
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<MessageIcon />}
                                    onClick={handleStartChat}
                                    size="large"
                                    fullWidth
                                    sx={{
                                        borderRadius: 2,
                                        maxWidth: { xs: '100%', sm: 200 },
                                        whiteSpace: 'nowrap',
                                        backgroundColor: 'primary.main',
                                        '&:hover': {
                                            backgroundColor: 'primary.dark',
                                        }
                                    }}
                                    disabled={contactLoading || isBlocked || user._id === currentUser?.uid || !canContact || user.isSample}
                                >
                                    {contactLoading ? <CircularProgress size={24} /> : '連絡する'}
                                </Button>
                            </Box>
                        )}

                        {user.isSample && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                サンプルユーザーには連絡できません
                            </Typography>
                        )}
                    </Paper>

                    {/* 掲載中の物件 */}
                    {!roomsLoading && userRooms.length > 0 && (
                        <Paper
                            elevation={0}
                            sx={{
                                mt: 3,
                                p: 4,
                                borderRadius: 2,
                                backgroundColor: 'grey.50'
                            }}
                        >
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{
                                    color: 'primary.main',
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 3
                                }}
                            >
                                <HomeIcon sx={{ mr: 1 }} />
                                {profile?.nickname}さんの掲載中の物件
                            </Typography>
                            <Grid container spacing={2}>
                                {userRooms.map((room, index) => (
                                    <Grid item xs={12} key={room._id}>
                                        <Card
                                            elevation={1}
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: 'background.paper',
                                                    transform: 'translateY(-2px)',
                                                    transition: 'all 0.2s ease-in-out'
                                                }
                                            }}
                                        >
                                            <CardContent sx={{ p: 2 }}>
                                                <Box
                                                    component={Link}
                                                    to={`/rooms/${room._id}`}
                                                    sx={{
                                                        display: 'flex',
                                                        textDecoration: 'none',
                                                        color: 'inherit'
                                                    }}
                                                >
                                                    <Box
                                                        component="img"
                                                        src={room.images?.[0] || '/default-room.jpg'}
                                                        alt={room.title}
                                                        sx={{
                                                            width: 80,
                                                            height: 80,
                                                            borderRadius: 1,
                                                            objectFit: 'cover',
                                                            mr: 2
                                                        }}
                                                    />
                                                    <Box>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                                                            ¥{room.rentamount?.toLocaleString()}/月
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            {room.location?.prefecture} {room.location?.city}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {room.propertyType} • {room.roomType}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    )}

                    <Dialog
                        open={openDialog}
                        onClose={handleBlockDialogClose}
                    >
                        <DialogTitle>
                            {blockAction === 'block' ? 'ユーザーをブロック' : 'ブロック解除'}
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                {blockAction === 'block'
                                    ? `${profile?.nickname}さんをブロックしますか？\nブロックすると、このユーザーとのメッセージのやり取りができなくなります。`
                                    : `${profile?.nickname}さんのブロックを解除しますか？`
                                }
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleBlockDialogClose}>キャンセル</Button>
                            <Button
                                onClick={handleBlockToggle}
                                color={blockAction === 'block' ? 'error' : 'primary'}
                                variant="contained"
                            >
                                {blockAction === 'block' ? 'ブロックする' : 'ブロック解除'}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* 通報ダイアログ */}
                    <ReportDialog
                        open={reportDialogOpen}
                        onClose={() => setReportDialogOpen(false)}
                        targetType="user"
                        targetId={id}
                    />
                </>
            )}
        </Container>
    );
}

export default UserDetail; 